import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { chatApi } from '../services/api'
import { updateGlobalAuthState } from '../services/authState'
import type { FactCheckResponse } from '@treu/shared'
import type { UserProfile as User } from '@treu/shared'

// API response types
interface ApiChatMessage {
  id: string
  message_type: 'user' | 'bot' | 'typing'
  content?: string
  result?: FactCheckResponse
  created_at: string
}

interface AddMessageResponse {
  userMessage: ApiChatMessage
  botResponse: ApiChatMessage | null
  updatedUser?: User
}

// Chat message interface
export interface ChatMessage {
  id: string
  type: 'user' | 'bot' | 'typing'
  text: string | undefined
  result: FactCheckResponse | undefined
  timestamp: Date
}

// Thread interface (without messages - lazy loaded separately)
export interface ChatThread {
  id: string
  title: string
  createdAt: Date
  updatedAt: Date
}

// Query keys for cache management
export const chatKeys = {
  all: ['chats'] as const,
  threads: ['chats', 'threads'] as const,
  thread: (id: string) => ['chats', 'thread', id] as const,
  messages: (threadId: string) => ['chats', 'messages', threadId] as const,
}

// Hook for chat threads (NO messages - just the list)
export function useChatThreads() {
  return useQuery<ChatThread[], Error>({
    queryKey: chatKeys.threads,
    queryFn: async (): Promise<ChatThread[]> => {
      const threads = await chatApi.getThreads()

      if (threads.length === 0) {
        // Create welcome thread if none exist
        const welcomeThread = await chatApi.createThread()
        return [{
          id: welcomeThread.id,
          title: welcomeThread.title,
          createdAt: new Date(welcomeThread.created_at),
          updatedAt: new Date(welcomeThread.updated_at)
        }]
      }

      // Just return thread metadata - NO message fetching here
      return threads.map(thread => ({
        id: thread.id,
        title: thread.title,
        createdAt: new Date(thread.created_at),
        updatedAt: new Date(thread.updated_at)
      }))
    },
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    cacheTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
    refetchOnWindowFocus: true, // Refetch when user tabs back to browser
    refetchOnMount: true, // Refetch when component mounts
  })
}

// Hook for messages of a specific thread (lazy loaded)
export function useChatMessages(threadId: string | null) {
  return useQuery<ChatMessage[], Error>({
    queryKey: chatKeys.messages(threadId ?? ''),
    queryFn: async (): Promise<ChatMessage[]> => {
      if (!threadId) return []
      const messages = await chatApi.getMessages(threadId)
      return messages.map(msg => ({
        id: msg.id,
        type: msg.message_type as 'user' | 'bot' | 'typing',
        text: msg.content ?? undefined,
        result: msg.result ?? undefined,
        timestamp: new Date(msg.created_at)
      }))
    },
    enabled: !!threadId, // Only fetch if threadId is provided
    staleTime: 1000 * 30, // Consider data fresh for 30 seconds
    cacheTime: 1000 * 60 * 10, // Keep in cache for 10 minutes
    refetchOnWindowFocus: true, // Refetch messages when user switches back
    refetchOnMount: true, // Refetch when switching threads
  })
}

// Hook for creating new threads
export function useCreateThread() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: chatApi.createThread,
    onSuccess: (newThread) => {
      // Update threads cache
      queryClient.setQueryData<ChatThread[]>(chatKeys.threads, (old = []) => {
        const newChatThread: ChatThread = {
          id: newThread.id,
          title: newThread.title,
          createdAt: new Date(newThread.created_at),
          updatedAt: new Date(newThread.updated_at)
        }
        return [newChatThread, ...old]
      })
    }
  })
}

// Hook for deleting threads
export function useDeleteThread() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (threadId: string) => chatApi.deleteThread(threadId),
    onSuccess: (_, threadId) => {
      // Remove from threads cache
      queryClient.setQueryData<ChatThread[]>(chatKeys.threads, (old = []) =>
        old.filter(thread => thread.id !== threadId)
      )
      // Remove messages cache for this thread
      queryClient.removeQueries({ queryKey: chatKeys.messages(threadId) })
    }
  })
}

// Hook for updating thread titles
export function useUpdateThread() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ threadId, title }: { threadId: string; title: string }) =>
      chatApi.updateThread(threadId, title),
    onSuccess: (updatedThread, { threadId, title }) => {
      // Update threads cache
      queryClient.setQueryData<ChatThread[]>(chatKeys.threads, (old = []) =>
        old.map(thread =>
          thread.id === threadId
            ? { ...thread, title, updatedAt: new Date(updatedThread.updated_at) }
            : thread
        )
      )
    }
  })
}

// Hook for adding messages to threads
export function useAddMessage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ threadId, messageType, content, result }: {
      threadId: string
      messageType: 'user' | 'bot' | 'typing'
      content?: string
      result?: FactCheckResponse
    }) => chatApi.addMessage(threadId, messageType, content, result),
    onSuccess: (response, { threadId, messageType }) => {
      if (messageType === 'user') {
        // Handle new API response format for user messages
        const typedResponse = response as unknown as AddMessageResponse
        const { userMessage, botResponse, updatedUser } = typedResponse

        // Build array of messages to add
        const messagesToAdd: ChatMessage[] = [{
          id: userMessage.id,
          type: userMessage.message_type,
          text: userMessage.content ?? undefined,
          result: userMessage.result ?? undefined,
          timestamp: new Date(userMessage.created_at)
        }]

        // Add bot response if it exists
        if (botResponse) {
          messagesToAdd.push({
            id: botResponse.id,
            type: botResponse.message_type,
            text: botResponse.content ?? undefined,
            result: botResponse.result ?? undefined,
            timestamp: new Date(botResponse.created_at)
          })
        }

        // Update messages cache for this thread
        queryClient.setQueryData<ChatMessage[]>(chatKeys.messages(threadId), (old = []) =>
          [...old, ...messagesToAdd]
        )

        // Update thread's updatedAt in threads cache
        queryClient.setQueryData<ChatThread[]>(chatKeys.threads, (old = []) =>
          old.map(thread =>
            thread.id === threadId
              ? { ...thread, updatedAt: new Date() }
              : thread
          )
        )

        // If there's a bot response with updated user data, update all caches
        if (botResponse && updatedUser) {
          // Directly update the global auth state with fresh user data from database
          updateGlobalAuthState({
            status: 'authenticated',
            user: updatedUser
          })

          // Invalidate analytics to refresh with new data
          queryClient.invalidateQueries({ queryKey: ['analytics'], exact: false })
        }
      } else {
        // For bot/typing messages, handle as single message (legacy format)
        const newMessage = response as ApiChatMessage
        queryClient.setQueryData<ChatMessage[]>(chatKeys.messages(threadId), (old = []) =>
          [...old, {
            id: newMessage.id,
            type: newMessage.message_type,
            text: newMessage.content ?? undefined,
            result: newMessage.result ?? undefined,
            timestamp: new Date(newMessage.created_at)
          }]
        )

        // Update thread's updatedAt
        queryClient.setQueryData<ChatThread[]>(chatKeys.threads, (old = []) =>
          old.map(thread =>
            thread.id === threadId
              ? { ...thread, updatedAt: new Date() }
              : thread
          )
        )
      }
    }
  })
}

// Hook for verifying pending queries after upgrade
export function useVerifyPendingQuery() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ threadId, messageId }: { threadId: string; messageId: string }) =>
      chatApi.verifyPendingQuery(threadId, messageId),
    onSuccess: (updatedMessage, { threadId, messageId }) => {
      // Update the message in cache with the verification result
      queryClient.setQueryData<ChatMessage[]>(chatKeys.messages(threadId), (old = []) =>
        old.map(msg =>
          msg.id === messageId
            ? {
                id: updatedMessage.id,
                type: updatedMessage.message_type as 'user' | 'bot' | 'typing',
                text: updatedMessage.content ?? undefined,
                result: updatedMessage.result ?? undefined,
                timestamp: new Date(updatedMessage.created_at)
              }
            : msg
        )
      )
    }
  })
}
